import { useState } from 'react';
import axios from '../../../../api/axios';
import { getLanguageConfig } from '../../../../utils/code/languageConfig';

/**
 * 代码执行管理 Hook
 * 负责处理代码的运行、执行和提交
 */
export function useCodeExecution(question, questionId) {
  const [running, setRunning] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [executeResult, setExecuteResult] = useState(null);
  const [result, setResult] = useState(null);

  const createCodeFile = (code, language) => {
    const lang = language || question?.language || 'python';
    const langConfig = getLanguageConfig(lang);
    const blob = new Blob([code], { type: langConfig.mimeType });
    return new File([blob], `solution.${langConfig.extension}`, { type: langConfig.mimeType });
  };

  const runCode = async (code, testType = 'public') => {
    if (!code.trim()) {
      throw new Error('请先编写代码');
    }

    setRunning(true);
    setRunResult(null);
    setExecuteResult(null);

    try {
      const file = createCodeFile(code, question?.language);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('questionId', questionId);
      formData.append('testType', testType);

      const response = await axios.post('/submissions/run', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setRunResult(response.data);
      return response.data;
    } catch (error) {
      console.error('Error running code:', error);
      const errorResult = {
        status: 'error',
        errorMessage: error.response?.data?.error || '运行失败，请重试',
        testResults: { attempted: 0, failed: 0, passed: 0 }
      };
      setRunResult(errorResult);
      throw error;
    } finally {
      setRunning(false);
    }
  };

  const executeCode = async (code) => {
    if (!code.trim()) {
      throw new Error('请先编写代码');
    }

    setExecuting(true);
    setExecuteResult(null);
    setRunResult(null);

    try {
      const file = createCodeFile(code, question?.language);
      const formData = new FormData();
      formData.append('file', file);
      if (question?.id) {
        formData.append('questionId', question.id);
      }

      const response = await axios.post('/submissions/execute', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setExecuteResult(response.data);
      return response.data;
    } catch (error) {
      console.error('Error executing code:', error);
      const errorResult = {
        status: 'error',
        errorMessage: error.response?.data?.error || '执行失败，请重试',
        output: ''
      };
      setExecuteResult(errorResult);
      throw error;
    } finally {
      setExecuting(false);
    }
  };

  const submitCode = async (code) => {
    if (!code.trim()) {
      throw new Error('请先编写代码');
    }

    setSubmitting(true);
    setResult(null);

    try {
      const file = createCodeFile(code, question?.language);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('questionId', questionId);

      const response = await axios.post('/submissions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const submission = response.data.submission;
      setResult(submission);
      return submission;
    } catch (error) {
      console.error('Error submitting:', error);
      const errorResult = {
        status: 'error',
        errorMessage: error.response?.data?.error || '提交失败，请重试'
      };
      setResult(errorResult);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const clearResults = () => {
    setRunResult(null);
    setExecuteResult(null);
    setResult(null);
  };

  return {
    running,
    executing,
    submitting,
    runResult,
    executeResult,
    result,
    runCode,
    executeCode,
    submitCode,
    clearResults
  };
}

